import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { CommunityGroupV2Dto, ImageDto } from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { GroupIcon } from "@/components/GroupIcon/GroupIcon";
import { Award01Icon } from "@/components/Icons/Award01Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { UserAvatarList } from "@/components/UserAvatarList/UserAvatarList";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Heading } from "@/design-system/Heading";
import { Text } from "@/design-system/Text";
import { ToggleButton } from "@/design-system/ToggleButton";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

interface Props {
  group: CommunityGroupV2Dto;
  projectDefaultImage: ImageDto | undefined;
  onFollow: (id: string) => void;
  onUnfollow: (id: string) => void;
}
export function OnboardingGroup({ group, projectDefaultImage, onFollow, onUnfollow }: Props): React.ReactNode {
  const { t } = useTranslation();
  const resolveImage = useImageResolver();

  const handleToggleFollow = () => {
    if (group.isMember) {
      onUnfollow(group.id);
    } else {
      onFollow(group.id);
    }
  };

  const image = !group.isResidentGroup && group.image ? group.image : projectDefaultImage;

  return (
    <Card density="compact" borderTone="secondary" variant="outlined">
      <RuntimeBackgroundImage imageUrl={image ? resolveImage(image, "intrinsic") : bgPlaceholder}>
        {!group.isResidentGroup && (
          <span className="flex size-thumbnail-compact items-center justify-center rounded-md bg-background-default">
            <GroupIcon size={24} icon={group.icon} />
          </span>
        )}
      </RuntimeBackgroundImage>
      <Heading level={4}>{group.name}</Heading>
      <Text as="p" variant="caption">
        {group.description}
      </Text>
      {group.members.items.length > 0 && (
        <UserAvatarList
          limit={3}
          images={group.members.items.filter((member) => member.avatar !== undefined).map((member) => member.avatar!)}
        />
      )}
      <footer className="flex justify-end pt-space-sm">
        {group.isOwner ? (
          <Button data-testid="group-admin-btn" icon={<Award01Icon />} disabled={true}>
            {t("model.interest-group.owner")}
          </Button>
        ) : (
          <ToggleButton
            fullWidth="untilSm"
            data-testid="group-action-follow-unfollow"
            config={{
              checked: {
                label: t("page.onboarding.groups.unfollow"),
                icon: <CheckIcon />,
              },
              unchecked: {
                label: t("page.onboarding.groups.follow"),
              },
            }}
            isChecked={group.isMember}
            onClick={handleToggleFollow}
          />
        )}
      </footer>
    </Card>
  );
}
